import React from 'react';
import { NextPage } from 'next';
import config from '../../common/config';
import { fetchContentPageData } from '../../common/helpers/data/fetchContentPageData';
import { TransformedProjectNetDataType } from '../../common/helpers/data/transformProjectnetProjectData';
import { Post } from '../../queries/contentPage/postDetail';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import { RecommendedData } from '../../common/helpers/domain/recommendation';
import { OtherPreviewData } from '../../common/helpers/domain/otherPreview';
import { ProjectArticleData } from '../../queries/projectPage/projectArticles';
import { SeoMetaComposerData } from '../../common/helpers/domain/seoMetaComposerData';
import { DDListingsResponse } from '../../lib/ddListingTypes';
import ContentPage from '../contentPage/ContentPage';
import Error from '../_error';
import { HTTP_RESPONSE_CODES } from '../../common/helpers/constants';
import {
  hasValidPreviewAccess,
  getPreviewAccessCookie,
} from '../../common/helpers/auth/previewAccess';

interface PreviewPageProps {
  asPath: string;
  canonicalLink: string;
  post: Post;
  projectRelatedPostData?: ProjectArticleData;
  postOtherPreviewData?: OtherPreviewData;
  postRecommendedData?: RecommendedData;
  postWhatToReadData?: { posts: { items: Post[] } };
  projectnetProjectData?: TransformedProjectNetDataType;
  projectNetProjectHref?: string;
  seoMetaComposerData?: SeoMetaComposerData;
  ddListingsData?: DDListingsResponse | null;
}

const PreviewPage: NextPage<PreviewPageProps> = ({
  asPath,
  canonicalLink,
  post,
  projectRelatedPostData,
  postOtherPreviewData,
  postRecommendedData,
  postWhatToReadData,
  projectnetProjectData,
  projectNetProjectHref,
  seoMetaComposerData,
  ddListingsData,
}) => {
  if (!post) {
    return <Error statusCode={HTTP_RESPONSE_CODES.NotFound} />;
  }

  return (
    <ContentPage
      asPath={asPath}
      canonicalLink={canonicalLink}
      post={post}
      projectRelatedPostData={projectRelatedPostData}
      postOtherPreviewData={postOtherPreviewData}
      postRecommendedData={postRecommendedData}
      postWhatToReadData={postWhatToReadData}
      projectnetProjectData={projectnetProjectData}
      projectNetProjectHref={projectNetProjectHref}
      seoMetaComposerData={seoMetaComposerData}
      ddListingsData={ddListingsData}
      isPreview={true}
    />
  );
};

PreviewPage.getInitialProps = async ({
  store,
  query,
  asPath,
  apolloClient,
  req,
}: CustomNextPageContext): Promise<PreviewPageProps> => {
  const { id } = query;
  const canonicalLink = config.baseUrl + asPath.substring(1);

  // Preview URLs are /preview/:id/, so the article id comes straight from the query
  const articleId = id as string;

  const fetchResult = await fetchContentPageData(
    articleId,
    apolloClient,
    store,
    asPath,
  );

  if (!fetchResult) {
    return {
      asPath,
      canonicalLink,
      post: null,
    };
  }

  const { post } = fetchResult;

  if (post.status === 'private') {
    const cookieValue = getPreviewAccessCookie(req);

    if (!hasValidPreviewAccess(cookieValue)) {
      // Render as not-found rather than the private post's data. This
      // covers both SSR (initial load) and client-side nav uniformly,
      // since a hard res.redirect is only possible during SSR.
      return {
        asPath,
        canonicalLink,
        post: null,
      };
    }
  }

  return {
    asPath,
    canonicalLink,
    ...fetchResult,
  };
};

export default PreviewPage;
